import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { View, StyleSheet, Animated } from "react-native";
import { X } from "@phosphor-icons/react";
import { useGetPostsByThreadQuery, useCreatePostMutation } from "../../../graphql/generated/graphql";

import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import TextEditor from "../../TextEditor/TextEditor";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import ParticipationOverview from "../ParticipationOverview/ParticipationOverview";
import AdminPostResponsesContainer from "../../../containers/adminDashboard/AdminPostResponsesContainer/AdminPostResponsesContainer";
import { PaperPlane } from "@phosphor-icons/react/dist/ssr/PaperPlane";
import { useAuth } from "../../../hooks/useAuth";

interface DiscussionPanelProps {
  isOpen: boolean;
  onClose: () => void;
  selectedThread: {
    id: string;
    title: string;
    courseId?: string;
    [key: string]: unknown;
  } | null;
}

interface Author {
  id: string;
  name: string;
}

interface Post {
  id: string;
  message: string;
  depth: number;
  threadId: string;
  created: string;
  lastModified: string;
  author: Author;
  replies: Post[];
  replyTo?: {
    id: string;
    message: string;
    author: Author;
  };
}

const DiscussionPanel: React.FC<DiscussionPanelProps> = ({ isOpen, onClose, selectedThread }) => {
  const [slideAnim] = useState(new Animated.Value(0));
  const [responseContent, setResponseContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const [isReplySubmitting, setIsReplySubmitting] = useState(false);

  const [createPostMutation] = useCreatePostMutation();
  const { meData } = useAuth();

  const currentUser = meData?.me
    ? {
        id: meData.me.id,
        name: meData.me.name || "Unknown User",
        role: meData.me.isFaculty ? "faculty" : "student",
      }
    : null;

  const {
    data: postsData,
    loading,
    error,
    refetch,
  } = useGetPostsByThreadQuery({
    variables: {
      threadId: selectedThread?.id || "",
      page: 1,
      pageSize: 50,
    },
    skip: !selectedThread?.id || !isOpen,
  });

  // Process posts data when it changes
  useEffect(() => {
    if (postsData?.getPostsByThread?.data) {
      const allPosts = postsData.getPostsByThread.data;

      const transformedPosts: Post[] = allPosts
        .filter((post) => post.depth > 0) // Only show response posts (depth 1+), not the initial post
        .map((post) => ({
          id: post.id,
          message: post.message,
          depth: post.depth,
          threadId: post.threadId,
          created: post.created,
          lastModified: post.lastModified,
          author: {
            id: post.author.id,
            name: post.author.name,
          },
          replies:
            post.replies?.map((reply) => ({
              id: reply.id,
              message: reply.message,
              depth: reply.depth,
              threadId: post.threadId,
              created: reply.created,
              lastModified: reply.created,
              author: {
                id: reply.author.id,
                name: reply.author.name,
              },
              replies: [],
            })) || [],
          replyTo: post.replyTo
            ? {
                id: post.replyTo.id,
                message: post.replyTo.message,
                author: {
                  id: post.replyTo.author.id,
                  name: post.replyTo.author.name,
                },
              }
            : undefined,
        }));

      setPosts(transformedPosts);
    }
  }, [postsData]);

  useEffect(() => {
    if (isOpen) {
      Animated.timing(slideAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: false,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }
  }, [isOpen, slideAnim]);

  const handleClose = () => {
    setResponseContent("");
    setReplyingTo(null);
    setReplyContent("");
    setExpandedReplies(new Set());
    onClose();
  };

  const handleCancel = () => {
    setResponseContent("");
  };

  const handleSubmitResponse = async () => {
    if (!responseContent.trim() || !selectedThread || !currentUser) return;

    setIsSubmitting(true);

    try {
      console.log("Creating post:", {
        message: responseContent,
        threadId: selectedThread.id,
      });

      // Get the initial post (depth 0) to reply to
      const initialPost = postsData?.getPostsByThread?.data?.find((post) => post.depth === 0);

      const result = await createPostMutation({
        variables: {
          input: {
            message: responseContent.trim(),
            threadId: selectedThread.id,
            replyToPostId: initialPost?.id,
          },
        },
      });

      if (!result.data?.createPost?.success) {
        throw new Error(result.data?.createPost?.message || "Failed to create response");
      }

      setResponseContent("");
      await refetch();
    } catch (error) {
      console.error("Error creating post:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !selectedThread) {
    return null;
  }

  if (!currentUser) {
    return null;
  }

  const courseId = selectedThread.courseId;

  return createPortal(
    <Animated.View style={styles.overlay}>
      <Animated.View
        style={[
          styles.panel,
          {
            transform: [
              {
                translateX: slideAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [800, 0],
                }),
              },
            ],
          },
        ]}>
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <div style={{ flexShrink: 0 }}>
            <View style={styles.header}>
              <CustomText
                text={selectedThread.title || "Discussion"}
                size="l"
                weight="bold"
                useTranslationText={false}
              />
              <CustomButton
                onPress={handleClose}
                styleType="transparent"
                style={styles.closeButton}
                leftIcon={<X size={24} color="#6B7280" />}
                text=""
                useTranslationText={false}
              />
            </View>
          </div>

          {/* Scrollable Content */}
          <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
            {/* Response Section */}
            <View style={styles.section}>
              <CustomText
                text="Add Response"
                size="m"
                weight="bold"
                style={styles.sectionTitle}
                useTranslationText={false}
              />

              <CustomSpacing type="vertical" size="m" />

              <View style={styles.responseContainer}>
                <View style={styles.editorContainer}>
                  <TextEditor
                    value={responseContent}
                    onChange={setResponseContent}
                    placeholder="Type your response here..."
                    disabled={isSubmitting}
                  />
                </View>

                <CustomSpacing type="vertical" size="m" />

                <View style={styles.buttonContainer}>
                  <CustomButton
                    text="Post Response"
                    size="m"
                    styleType="primary"
                    disabled={isSubmitting || !responseContent.trim()}
                    onPress={handleSubmitResponse}
                    useTranslationText={false}
                    leftIcon={<PaperPlane size={20} color="#FFFFFF" weight="bold" />}
                    bold={true}
                  />
                  <CustomButton
                    text="Cancel"
                    size="m"
                    styleType="primaryLight"
                    disabled={isSubmitting}
                    onPress={handleCancel}
                    useTranslationText={false}
                    bold={true}
                  />
                </View>
              </View>
            </View>

            <CustomSpacing type="vertical" size="xl" />

            {/* Participation Overview Section - Only show for faculty */}
            {currentUser.role === "faculty" && (
              <>
                <ParticipationOverview threadId={selectedThread?.id} courseId={courseId} />
                <CustomSpacing type="vertical" size="xl" />
              </>
            )}

            {/* Posts Section */}
            <AdminPostResponsesContainer
              posts={posts}
              setPosts={setPosts}
              selectedThread={selectedThread}
              currentUser={currentUser}
              replyingTo={replyingTo}
              setReplyingTo={setReplyingTo}
              replyContent={replyContent}
              setReplyContent={setReplyContent}
              expandedReplies={expandedReplies}
              setExpandedReplies={setExpandedReplies}
              isReplySubmitting={isReplySubmitting}
              setIsReplySubmitting={setIsReplySubmitting}
              refetch={refetch}
            />
          </div>
        </div>
      </Animated.View>
    </Animated.View>,
    document.body
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1000,
  },
  panel: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: 800,
    backgroundColor: "#FFFFFF",
    borderLeftWidth: 1,
    borderLeftColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: {
      width: -2,
      height: 0,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 100,
    height: "100vh",
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
  },
  closeButton: {
    padding: 8,
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    color: "#101828",
  },
  responseContainer: {
    backgroundColor: "#F9FAFB",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 20,
  },
  editorContainer: {
    minHeight: 120,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  buttonContainer: {
    flexDirection: "row",
    gap: 12,
  },
});

export default DiscussionPanel;
